import React, { useState } from 'react';
import {
  CheckCircle,
  Edit3,
  Link as LinkIcon,
  MessageSquare,
  Package,
  Plus,
  RotateCcw,
  Send,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Proposal } from '@/apis/extended/proposalApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ComprehensiveClauseData } from '@/utils/comprehensiveClauseMapping';

interface LinkedClause {
  clauseId: string;
  clauseHierarchicalId: string;
  clauseTitle: string;
  condition: 'if_accepted' | 'if_rejected';
  proposedValue: any;
  currentValue?: any;
  description?: string;
}

interface ProposalClauseDisplayProps {
  proposals: Proposal[];
  allClauses: ComprehensiveClauseData[];
  userRole: 'buyer' | 'seller' | 'agent';
  onProposalCreated?: (
    clauseId: string,
    clauseHierarchicalId: string,
    originalValue: any,
    proposedValue: any,
    message?: string,
    linkedClauses?: LinkedClause[]
  ) => void;
  onProposalRespond?: (
    proposalId: string,
    status: 'accepted' | 'rejected',
    message?: string
  ) => void;
  onAddToBatch?: (
    clauseId: string,
    clauseHierarchicalId: string,
    title: string,
    originalValue: any,
    proposedValue: any,
    message?: string
  ) => void;
  selectedBatchIds?: Set<string>;
}

export const ProposalClauseDisplay: React.FC<ProposalClauseDisplayProps> = ({
  proposals,
  allClauses,
  userRole,
  onProposalCreated,
  onProposalRespond,
  onAddToBatch,
  selectedBatchIds = new Set(),
}) => {
  const [editingProposal, setEditingProposal] = useState<string | null>(null);
  const [counterValues, setCounterValues] = useState<Record<string, any>>({});

  // Calculate total price impact from proposals
  const calculateTotalPriceImpact = () => {
    let totalImpact = 0;
    proposals.forEach((proposal) => {
      if (
        proposal.clauseId === 'purchase-price' ||
        proposal.clauseId === 'offer-price'
      ) {
        const originalPrice = Number(proposal.originalValue) || 0;
        const proposedPrice = Number(proposal.proposedValue) || 0;
        totalImpact += proposedPrice - originalPrice;
      }
      if (
        proposal.clauseId === 'seller-assistance-amount' ||
        proposal.clauseId === 'seller-assistance'
      ) {
        const originalAmount = Number(proposal.originalValue) || 0;
        const proposedAmount = Number(proposal.proposedValue) || 0;
        totalImpact += proposedAmount - originalAmount; // Seller paying more = cost to seller
      }
    });
    return totalImpact;
  };

  const priceImpact = calculateTotalPriceImpact();
  const [counterMessages, setCounterMessages] = useState<
    Record<string, string>
  >({});
  const [showLinkClause, setShowLinkClause] = useState<string | null>(null);
  const [linkedClauses, setLinkedClauses] = useState<
    Record<string, LinkedClause[]>
  >({});

  // Group proposals by clause ID
  const proposalsByClause = proposals.reduce((acc, proposal) => {
    if (!acc[proposal.clauseId]) {
      acc[proposal.clauseId] = [];
    }
    acc[proposal.clauseId].push(proposal);
    return acc;
  }, {} as Record<string, Proposal[]>);

  // Get clause info from comprehensive clauses
  const getClauseInfo = (clauseId: string) => {
    return allClauses.find((c) => c.id === clauseId);
  };

  const handleAcceptProposal = (proposalId: string) => {
    onProposalRespond?.(proposalId, 'accepted');
    toast.success('Proposal accepted');
  };

  const handleRejectProposal = (proposalId: string) => {
    onProposalRespond?.(
      proposalId,
      'rejected_no_counter',
      'Proposal denied without counter-offer'
    );
    toast.error('Proposal denied (no counter submitted)');
  };

  const handleCounterProposal = (proposal: Proposal) => {
    const counterValue = counterValues[proposal._id];
    const message = counterMessages[proposal._id];
    const linked = linkedClauses[proposal._id] || [];

    if (!counterValue) {
      toast.warning('Please provide a counter value');
      return;
    }

    onProposalCreated?.(
      proposal.clauseId,
      proposal.clauseHierarchicalId,
      proposal.proposedValue,
      counterValue,
      message,
      linked
    );

    setEditingProposal(null);
    setCounterValues({});
    setCounterMessages({});
    setLinkedClauses({});
    toast.success('Counter-proposal submitted');
  };

  const handleAddLinkedClause = (proposalId: string) => {
    setShowLinkClause(proposalId);
  };

  const addLinkedClause = (proposalId: string, linkedClause: LinkedClause) => {
    setLinkedClauses((prev) => ({
      ...prev,
      [proposalId]: [...(prev[proposalId] || []), linkedClause],
    }));
    setShowLinkClause(null);
    toast.success(`Linked clause: ${linkedClause.clauseTitle}`);
  };

  const removeLinkedClause = (proposalId: string, index: number) => {
    setLinkedClauses((prev) => ({
      ...prev,
      [proposalId]: (prev[proposalId] || []).filter((_, i) => i !== index),
    }));
  };

  const renderValueInput = (proposal: Proposal, currentValue: any) => {
    // Boolean (Yes/No) - use select dropdown
    if (typeof currentValue === 'boolean') {
      return (
        <Select
          value={
            counterValues[proposal._id]?.toString() || currentValue.toString()
          }
          onValueChange={(value) =>
            setCounterValues({
              ...counterValues,
              [proposal._id]: value === 'true',
            })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    // Number (currency, percentage, etc.)
    if (typeof currentValue === 'number') {
      return (
        <Input
          type="number"
          value={counterValues[proposal._id] ?? currentValue}
          onChange={(e) =>
            setCounterValues({
              ...counterValues,
              [proposal._id]: Number(e.target.value),
            })
          }
          step="any"
          placeholder="Enter new value"
        />
      );
    }

    // Date - detect date strings
    const isDate =
      typeof currentValue === 'string' &&
      (currentValue.match(/^\d{4}-\d{2}-\d{2}/) ||
        currentValue.includes('T') ||
        !isNaN(Date.parse(currentValue)));

    if (isDate) {
      const dateValue = counterValues[proposal._id]
        ? new Date(counterValues[proposal._id]).toISOString().split('T')[0]
        : new Date(currentValue).toISOString().split('T')[0];

      return (
        <Input
          type="date"
          value={dateValue}
          onChange={(e) =>
            setCounterValues({
              ...counterValues,
              [proposal._id]: e.target.value,
            })
          }
        />
      );
    }

    // Default to text input
    return (
      <Input
        type="text"
        value={counterValues[proposal._id] ?? currentValue}
        onChange={(e) =>
          setCounterValues({
            ...counterValues,
            [proposal._id]: e.target.value,
          })
        }
        placeholder="Enter new value"
      />
    );
  };

  return (
    <div className="space-y-4">
      {/* Proposal summary moved to NegotiationStatusBar component */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Active Proposals
            <Badge variant="secondary" className="ml-auto">
              {proposals.length}{' '}
              {proposals.length === 1 ? 'Proposal' : 'Proposals'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {proposals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No active proposals</p>
              <p className="text-sm mt-1">All clauses are agreed upon</p>
            </div>
          ) : (
            Object.entries(proposalsByClause).map(
              ([clauseId, clauseProposals]) => {
                const clauseInfo = getClauseInfo(clauseId);
                const latestProposal =
                  clauseProposals[clauseProposals.length - 1];
                const isMyTurn =
                  latestProposal.responseRequiredFrom === userRole;
                const proposalLinkedClauses =
                  linkedClauses[latestProposal._id] || [];

                // Find the most recent proposal from the OTHER party (for "accept their offer" option)
                const theirLatestProposal = clauseProposals
                  .slice()
                  .reverse()
                  .find(
                    (p) => p.proposedBy !== userRole && p.status === 'pending'
                  );

                return (
                  <Card
                    key={clauseId}
                    className={`${
                      isMyTurn
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg flex items-center gap-2">
                            {clauseInfo?.title || clauseId}
                            {latestProposal.status === 'pending' && (
                              <Badge
                                className={
                                  isMyTurn ? 'bg-blue-600' : 'bg-gray-400'
                                }
                              >
                                {isMyTurn
                                  ? 'Your Turn'
                                  : 'Waiting for Response'}
                              </Badge>
                            )}
                            {latestProposal.batchId && (
                              <Badge
                                variant="secondary"
                                className="bg-purple-100 text-purple-800"
                              >
                                <Package className="h-3 w-3 mr-1" />
                                Part of Batch
                              </Badge>
                            )}
                          </CardTitle>
                          <p className="text-sm text-gray-600 mt-1">
                            {clauseInfo?.description || ''}
                          </p>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      {/* Proposal History */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Proposal History
                        </Label>
                        {clauseProposals.map((proposal, index) => (
                          <div
                            key={proposal._id}
                            className="pl-4 border-l-2 border-gray-300 py-2"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant={
                                      proposal.proposedBy === userRole
                                        ? 'default'
                                        : 'secondary'
                                    }
                                  >
                                    {proposal.proposedBy === userRole
                                      ? 'You'
                                      : proposal.proposedBy}
                                  </Badge>
                                  <span className="text-sm text-gray-600">
                                    proposed:{' '}
                                    <strong>
                                      {String(proposal.proposedValue)}
                                    </strong>
                                  </span>
                                </div>
                                {proposal.message && (
                                  <p className="text-sm text-gray-600 mt-1 italic">
                                    "{proposal.message}"
                                  </p>
                                )}
                                {proposal.linkedClauses &&
                                  proposal.linkedClauses.length > 0 && (
                                    <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded">
                                      <div className="flex items-center gap-1 text-xs font-bold text-purple-800 mb-2">
                                        <LinkIcon className="h-4 w-4" />
                                        {proposal.linkedClauses.length} Linked
                                        Condition
                                        {proposal.linkedClauses.length > 1
                                          ? 's'
                                          : ''}
                                      </div>
                                      <div className="space-y-2">
                                        {proposal.linkedClauses.map(
                                          (linked, i) => (
                                            <div
                                              key={i}
                                              className="bg-white rounded p-2 text-xs"
                                            >
                                              <div className="font-semibold text-purple-700 mb-1">
                                                {linked.condition ===
                                                'if_accepted'
                                                  ? '✓ If this proposal is accepted'
                                                  : '✗ If this proposal is rejected'}
                                              </div>
                                              <div className="text-gray-700">
                                                <span className="font-medium">
                                                  {linked.clauseId}:
                                                </span>{' '}
                                                {linked.currentValue !==
                                                  undefined && (
                                                  <span className="text-gray-500">
                                                    {String(
                                                      linked.currentValue
                                                    )}{' '}
                                                    →{' '}
                                                  </span>
                                                )}
                                                <span className="font-semibold text-purple-900">
                                                  {String(linked.proposedValue)}
                                                </span>
                                              </div>
                                              {linked.description && (
                                                <div className="text-gray-600 italic mt-1">
                                                  "{linked.description}"
                                                </div>
                                              )}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                              <Badge
                                variant={
                                  proposal.status === 'accepted'
                                    ? 'default'
                                    : proposal.status === 'rejected'
                                    ? 'destructive'
                                    : 'secondary'
                                }
                              >
                                {proposal.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Actions for My Turn */}
                      {isMyTurn && latestProposal.status === 'pending' && (
                        <div className="space-y-3 border-t pt-4">
                          {editingProposal === latestProposal._id ? (
                            <>
                              <div>
                                <Label className="text-sm font-medium">
                                  Counter Value
                                </Label>
                                <div className="mt-1">
                                  {renderValueInput(
                                    latestProposal,
                                    latestProposal.proposedValue
                                  )}
                                </div>
                              </div>

                              <div>
                                <Label className="text-sm font-medium">
                                  Message (Optional)
                                </Label>
                                <Textarea
                                  value={
                                    counterMessages[latestProposal._id] || ''
                                  }
                                  onChange={(e) =>
                                    setCounterMessages({
                                      ...counterMessages,
                                      [latestProposal._id]: e.target.value,
                                    })
                                  }
                                  placeholder="Explain your counter-proposal..."
                                  rows={2}
                                  className="mt-1"
                                />
                              </div>

                              {/* Linked Clauses Section */}
                              <div className="border-t pt-3">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-sm font-medium flex items-center gap-1">
                                    <LinkIcon className="h-4 w-4" />
                                    Linked Conditions
                                  </Label>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleAddLinkedClause(latestProposal._id)
                                    }
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add Link
                                  </Button>
                                </div>

                                {proposalLinkedClauses.length > 0 && (
                                  <div className="space-y-2 mb-3">
                                    {proposalLinkedClauses.map(
                                      (linked, index) => (
                                        <div
                                          key={index}
                                          className="flex items-center justify-between bg-purple-50 p-2 rounded"
                                        >
                                          <div className="flex-1 text-sm">
                                            <span className="font-medium text-purple-700">
                                              {linked.condition ===
                                              'if_accepted'
                                                ? 'If accepted:'
                                                : 'If rejected:'}
                                            </span>{' '}
                                            {linked.clauseTitle} ={' '}
                                            {String(linked.proposedValue)}
                                            {linked.description && (
                                              <span className="text-gray-600 italic ml-2">
                                                "{linked.description}"
                                              </span>
                                            )}
                                          </div>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() =>
                                              removeLinkedClause(
                                                latestProposal._id,
                                                index
                                              )
                                            }
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}

                                {showLinkClause === latestProposal._id && (
                                  <LinkClauseForm
                                    allClauses={allClauses}
                                    currentClauseId={clauseId}
                                    onAdd={(linked) =>
                                      addLinkedClause(
                                        latestProposal._id,
                                        linked
                                      )
                                    }
                                    onCancel={() => setShowLinkClause(null)}
                                  />
                                )}
                              </div>

                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleCounterProposal(latestProposal)
                                  }
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Submit Counter
                                </Button>
                                {theirLatestProposal &&
                                  theirLatestProposal._id !==
                                    latestProposal._id && (
                                    <Button
                                      size="sm"
                                      variant="default"
                                      onClick={() => {
                                        handleAcceptProposal(
                                          theirLatestProposal._id
                                        );
                                        setEditingProposal(null);
                                      }}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <ThumbsUp className="h-4 w-4 mr-2" />
                                      Or Accept $
                                      {theirLatestProposal.proposedValue}
                                    </Button>
                                  )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingProposal(null);
                                    setCounterValues({});
                                    setCounterMessages({});
                                    setLinkedClauses({});
                                    setShowLinkClause(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </>
                          ) : (
                            <div className="flex flex-col gap-2">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() =>
                                    handleAcceptProposal(latestProposal._id)
                                  }
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <ThumbsUp className="h-4 w-4 mr-2" />
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    setEditingProposal(latestProposal._id)
                                  }
                                >
                                  <Edit3 className="h-4 w-4 mr-2" />
                                  Counter
                                </Button>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() =>
                                    handleRejectProposal(latestProposal._id)
                                  }
                                  className="flex-1"
                                >
                                  <ThumbsDown className="h-4 w-4 mr-2" />
                                  Deny (No Counter)
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              }
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Sub-component for adding linked clauses
interface LinkClauseFormProps {
  allClauses: ComprehensiveClauseData[];
  currentClauseId: string;
  onAdd: (linked: LinkedClause) => void;
  onCancel: () => void;
}

const LinkClauseForm: React.FC<LinkClauseFormProps> = ({
  allClauses,
  currentClauseId,
  onAdd,
  onCancel,
}) => {
  const [selectedClauseId, setSelectedClauseId] = useState('');
  const [condition, setCondition] = useState<'if_accepted' | 'if_rejected'>(
    'if_accepted'
  );
  const [proposedValue, setProposedValue] = useState('');
  const [description, setDescription] = useState('');

  const availableClauses = allClauses.filter((c) => c.id !== currentClauseId);
  const selectedClause = allClauses.find((c) => c.id === selectedClauseId);

  // Get current value when clause is selected
  const currentValue = selectedClause?.value;

  // Detect value type for appropriate input
  const detectValueType = (
    value: any
  ): 'boolean' | 'number' | 'date' | 'enum' | 'text' => {
    if (typeof value === 'boolean' || value === 'yes' || value === 'no')
      return 'boolean';
    if (typeof value === 'number' || !isNaN(Number(value))) return 'number';
    if (value && (value.includes('-') || value.includes('/'))) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) return 'date';
    }
    // Check if it's a known enum value
    if (getEnumOptions(selectedClauseId).length > 0) return 'enum';
    return 'text';
  };

  // Get enum options for specific clauses
  const getEnumOptions = (clauseId: string): string[] => {
    const enumMappings: Record<string, string[]> = {
      'appraisal-contingency': [
        'at_least_purchase_price',
        'at_least_listing_price',
        'at_least_within_range',
      ],
      'financing-contingency': ['at_least_purchase_price', 'timeline'],
      'inspection-contingency': ['reserve_the_right', 'waive_the_right'],
      'post-close-occupancy': ['yes', 'no'],
      'copy-of-inspection': ['provide_full_copy', 'withhold_full_copy'],
      'inspection-presence': ['be_present', 'waive_right'],
      'property-condition': [
        'As Is',
        'Subject to Inspection',
        'Seller Repairs',
      ],
    };
    return enumMappings[clauseId] || [];
  };

  const valueType =
    currentValue !== undefined ? detectValueType(currentValue) : 'text';
  const enumOptions = getEnumOptions(selectedClauseId);

  const handleSubmit = () => {
    if (!selectedClauseId || !proposedValue) {
      toast.warning('Please select a clause and provide a value');
      return;
    }

    onAdd({
      clauseId: selectedClauseId,
      clauseHierarchicalId: selectedClause?.hierarchicalId || selectedClauseId,
      clauseTitle: selectedClause?.title || selectedClauseId,
      condition,
      proposedValue,
      currentValue,
      description: description || undefined,
    });
  };

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-purple-50">
      <div>
        <Label className="text-xs">Condition</Label>
        <Select value={condition} onValueChange={(v: any) => setCondition(v)}>
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="if_accepted">
              If this proposal is accepted
            </SelectItem>
            <SelectItem value="if_rejected">
              If this proposal is rejected
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Then change this clause</Label>
        <Select value={selectedClauseId} onValueChange={setSelectedClauseId}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select clause..." />
          </SelectTrigger>
          <SelectContent>
            {availableClauses.map((clause) => (
              <SelectItem key={clause.id} value={clause.id}>
                {clause.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedClause && currentValue !== undefined && (
        <div className="bg-blue-50 border border-blue-200 rounded p-2">
          <Label className="text-xs text-blue-700">Current Value</Label>
          <div className="text-sm font-medium text-blue-900">
            {String(currentValue)}
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs">To this value</Label>
        {valueType === 'enum' ? (
          <Select value={proposedValue} onValueChange={setProposedValue}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select option..." />
            </SelectTrigger>
            <SelectContent>
              {enumOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : valueType === 'boolean' ? (
          <Select value={proposedValue} onValueChange={setProposedValue}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select Yes or No" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        ) : valueType === 'date' ? (
          <Input
            type="date"
            value={proposedValue}
            onChange={(e) => setProposedValue(e.target.value)}
            className="mt-1"
          />
        ) : valueType === 'number' ? (
          <Input
            type="number"
            value={proposedValue}
            onChange={(e) => setProposedValue(e.target.value)}
            placeholder={
              currentValue !== undefined
                ? `Current: ${String(currentValue)}`
                : 'Enter number'
            }
            className="mt-1"
          />
        ) : (
          <Input
            type="text"
            value={proposedValue}
            onChange={(e) => setProposedValue(e.target.value)}
            placeholder={
              currentValue !== undefined
                ? `Current: ${String(currentValue)}`
                : 'New value'
            }
            className="mt-1"
          />
        )}
      </div>

      <div>
        <Label className="text-xs">Explanation (Optional)</Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., 'Price reduced to compensate for...'"
          rows={2}
          className="mt-1"
        />
      </div>

      <div className="flex gap-2">
        <Button size="sm" onClick={handleSubmit}>
          <Plus className="h-3 w-3 mr-1" />
          Add Link
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
